import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { updateConsent, trackPageView } from '../utils/gtag';

const CONSENT_KEY = 'cookie_consent';

type ConsentValue = 'granted' | 'denied' | null;

function getStoredConsent(): ConsentValue {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    if (value === 'granted' || value === 'denied') return value;
  } catch {
    // localStorage indisponible
  }
  return null;
}

function storeConsent(value: 'granted' | 'denied') {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // localStorage indisponible
  }
}

/**
 * Applique le consentement stocké au chargement
 * (appelé dans main.tsx pour restaurer le choix précédent)
 */
export function restoreConsent() {
  const stored = getStoredConsent();
  if (stored) {
    updateConsent(stored === 'granted');
  }
}

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // N'afficher que si l'utilisateur n'a pas encore fait de choix
    if (getStoredConsent() === null) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    storeConsent('granted');
    updateConsent(true);
    // Renvoyer le page_view car l'initial a été droppé (consent=denied au moment du chargement)
    trackPageView(window.location.pathname + window.location.search, document.title);
    setVisible(false);
  };

  const handleRefuse = () => {
    storeConsent('denied');
    updateConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] animate-in slide-in-from-bottom duration-500"
      style={{ maxWidth: 360, width: 'calc(100vw - 3rem)' }}
    >
      <div
        className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
        style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
      >
        <p className="text-sm font-medium text-gray-900 mb-1">{t('landing.cookies.title')}</p>
        <p className="text-sm text-gray-500 leading-relaxed mb-4">
          {t('landing.cookies.text')}{' '}
          <a href="/privacy" className="underline text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap">
            {t('landing.cookies.learn_more')}
          </a>
        </p>
        <div className="flex gap-3">
          {/* Bouton secondaire — style "Regarder la démo" de la landing */}
          <button
            onClick={handleRefuse}
            className="flex-1 inline-flex items-center justify-center px-4 py-2.5 border text-gray-900 rounded-full text-sm font-medium transition-all duration-300 relative group overflow-hidden"
            style={{ borderColor: '#F0F0F0', background: '#F8F8F8' }}
          >
            {t('landing.cookies.refuse')}
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
              style={{ background: 'linear-gradient(135deg, #8B5CF6, #EC4899)' }}
            />
          </button>
          {/* Bouton principal — style "Démarrer" de la landing */}
          <button
            onClick={handleAccept}
            className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 relative group overflow-hidden"
            style={{ background: '#000000', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}
          >
            <span className="relative z-10" style={{ color: '#FFFFFF' }}>{t('landing.cookies.accept')}</span>
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: '#1a1a1a' }}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
